import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLandingData } from "../../../hooks/useLandingData";

const rowOne = [
  { name: "Google Calendar", logo: "https://logo.clearbit.com/calendar.google.com" },
  { name: "Twilio", logo: "https://logo.clearbit.com/twilio.com" },
  { name: "Salesforce", logo: "https://logo.clearbit.com/salesforce.com" },
  { name: "HubSpot", logo: "https://logo.clearbit.com/hubspot.com" },
  { name: "Slack", logo: "https://logo.clearbit.com/slack.com" },
  { name: "Microsoft Teams", logo: "https://logo.clearbit.com/microsoft.com" },
  { name: "Zapier", logo: "https://logo.clearbit.com/zapier.com" },
  { name: "Stripe", logo: "https://logo.clearbit.com/stripe.com" },
  { name: "Notion", logo: "https://logo.clearbit.com/notion.so" },
  { name: "Monday.com", logo: "https://logo.clearbit.com/monday.com" },
];

const rowTwo = [
  { name: "Zendesk", logo: "https://logo.clearbit.com/zendesk.com" },
  { name: "WhatsApp Business", logo: "https://logo.clearbit.com/whatsapp.com" },
  { name: "Pipedrive", logo: "https://logo.clearbit.com/pipedrive.com" },
  { name: "Intercom", logo: "https://logo.clearbit.com/intercom.com" },
  { name: "ActiveCampaign", logo: "https://logo.clearbit.com/activecampaign.com" },
  { name: "Calendly", logo: "https://logo.clearbit.com/calendly.com" },
  { name: "Typeform", logo: "https://logo.clearbit.com/typeform.com" },
  { name: "Airtable", logo: "https://logo.clearbit.com/airtable.com" },
  { name: "ClickUp", logo: "https://logo.clearbit.com/clickup.com" },
  { name: "Freshdesk", logo: "https://logo.clearbit.com/freshdesk.com" },
];

function MarqueeRow({ items, reverse = false }: { items: typeof rowOne; reverse?: boolean }) {
  const doubled = [...items, ...items];
  return (
    <div className="overflow-hidden w-full">
      <div
        className="flex gap-4 w-max"
        style={{ animation: `marquee${reverse ? "Reverse" : ""} 30s linear infinite` }}
      >
        {doubled.map((item, i) => (
          <div
            key={`${item.name}-${i}`}
            className="flex items-center gap-3 bg-white border border-stone-100 rounded-2xl px-5 py-3.5 flex-shrink-0 hover:border-amber-200 hover:bg-amber-50 transition-colors duration-200 cursor-default"
          >
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
              <img
                src={item.logo}
                alt={item.name}
                className="w-7 h-7 object-contain"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                  const parent = e.currentTarget.parentElement;
                  if (parent) parent.innerHTML = `<i class="ri-plug-line text-stone-300 text-lg"></i>`;
                }}
              />
            </div>
            <span className="text-stone-600 text-sm font-medium whitespace-nowrap">{item.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function IntegrationsSection() {
  const { securityFeatures } = useLandingData();
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="integraciones" ref={ref} className="py-28 bg-white overflow-hidden">
      <style>{`
        @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        @keyframes marqueeReverse { 0% { transform: translateX(-50%); } 100% { transform: translateX(0); } }
      `}</style>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <span className="inline-block text-xs font-bold tracking-widest uppercase text-amber-600 mb-4">
            {t("int_label")}
          </span>
          <h2
            className="text-4xl lg:text-5xl font-bold text-stone-900 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("int_title_1")}
            <br />
            <span className="text-amber-500">{t("int_title_2")}</span>
          </h2>
          <p className="mt-4 text-stone-500 text-lg max-w-xl mx-auto">
            {t("int_subtitle")}
          </p>
        </div>

        {/* Marquee carousels */}
        <div className={`space-y-4 mb-6 transition-all duration-700 delay-100 ${visible ? "opacity-100" : "opacity-0"}`}>
          <MarqueeRow items={rowOne} />
          <MarqueeRow items={rowTwo} reverse />
        </div>

        {/* Counter badge */}
        <div className={`text-center mb-20 transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span className="inline-flex items-center gap-2 text-xs text-stone-400 bg-stone-50 border border-stone-100 rounded-full px-4 py-2">
            <i className="ri-links-line text-amber-500" />
            {t("int_counter")}
          </span>
        </div>

        {/* Security block */}
        <div
          className="rounded-3xl overflow-hidden"
          style={{
            backgroundImage: `url(https://public.readdy.ai/ai/img_res/57beb919142af3758b2f5d6bdb592043.jpg)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="bg-stone-900/90 backdrop-blur-sm p-10 lg:p-16">
            <div className="text-center mb-12">
              <span className="inline-block text-xs font-bold tracking-widest uppercase text-amber-400 mb-3">
                {t("int_sec_badge")}
              </span>
              <h3
                className="text-3xl lg:text-4xl font-bold text-white"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {t("int_sec_title")}
              </h3>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {securityFeatures.map((sec, i) => (
                <div
                  key={sec.title}
                  className={`p-6 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 transition-all duration-300 cursor-default ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
                  style={{ transitionDelay: `${400 + i * 80}ms` }}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-400/15 border border-amber-400/20 mb-4">
                    <i className={`${sec.icon} text-amber-400 text-xl`} />
                  </div>
                  <h4 className="text-white font-semibold text-sm mb-2">{sec.title}</h4>
                  <p className="text-stone-400 text-xs leading-relaxed">{sec.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap items-center justify-center gap-6">
              {["ISO 27001", "SOC 2 Type II", "GDPR", "HIPAA", "PCI DSS"].map((cert) => (
                <div key={cert} className="flex items-center gap-2 px-4 py-2 bg-white/8 border border-white/15 rounded-full">
                  <i className="ri-checkbox-circle-line text-amber-400 text-sm" />
                  <span className="text-white/80 text-xs font-semibold">{cert}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
